/**
 * What the persons, contact and delivery steps share: the way back to the
 * menu when there is nothing to order, the route a quote needs until the
 * customer picks one, the server's price for exactly what is on screen, and
 * the bar that shows it.
 *
 * Pick & drop is the default where it is offered, to the one city served
 * when there is only one, so the total is shown from the first of the three
 * steps; the delivery step is where it can change.
 */

import { useEffect } from 'react';
import { StyleSheet, View } from 'react-native';

import { formatMeals, formatMoney, formatPeople, type TiffinQuote } from '@mfa/domain';
import { colors, radius, space } from '../../theme';
import { Skeleton } from '../../components/feedback/Skeleton';
import { Text } from '../../components/common/Text';
import { useAppConfig, useTiffinQuote } from '../../core/api/hooks';
import { useTiffinStore } from './tiffinStore';
import type { DonorScreenProps } from '../../app/navigation/types';
import { useT } from '../../core/i18n/LanguageProvider';

type Navigation = DonorScreenProps<'TiffinPersons'>['navigation'];

export function useTiffinCheckout(navigation: Pick<Navigation, 'replace'>) {
  const { data: config } = useAppConfig();
  const tiffin = config?.tiffin;
  const draft = useTiffinStore();
  const { quantities, serviceSlot, days, schedule, fulfilmentType, address } = draft;

  // Reachable only from the menu; a reload on the web loses the box.
  useEffect(() => {
    if (Object.keys(quantities).length === 0 || !serviceSlot || days === null || !schedule) {
      navigation.replace('TiffinMenu');
    }
  }, [quantities, serviceSlot, days, schedule, navigation]);

  const offered = tiffin?.fulfilmentTypes ?? [];
  useEffect(() => {
    if (offered.length && (!fulfilmentType || !offered.includes(fulfilmentType))) {
      draft.setFulfilment(offered[0]!);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offered.join(','), fulfilmentType]);

  const cities = tiffin?.delivery.cities ?? [];
  useEffect(() => {
    if (fulfilmentType === 'DOORSTEP_DELIVERY' && cities.length === 1 && !address.city) {
      draft.setAddress({ city: cities[0]! });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fulfilmentType, cities.join(','), address.city]);

  const quote = useTiffinQuote(draft.quoteRequest());
  useEffect(() => {
    draft.setQuote(quote.data ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote.data]);

  return { tiffin, draft, quote, total: draft.authoritativeTotal() };
}

/** "Rs. 610 × 2 People × 4 meals + Rs. 150 × 4 meals". */
function sum(quote: TiffinQuote): string {
  const meals = formatMeals(quote.deliveries);
  const boxes = `${formatMoney(quote.boxTotal)} × ${formatPeople(quote.persons)} × ${meals}`;
  if (quote.deliveryFee.amountMinor === 0) return boxes;
  const perMeal = {
    ...quote.deliveryFee,
    amountMinor: Math.round(quote.deliveryFee.amountMinor / Math.max(1, quote.deliveries)),
  };
  return `${boxes} + ${formatMoney(perMeal)} × ${meals}`;
}

export function TiffinTotalFooter({
  checkout,
  children,
}: {
  checkout: ReturnType<typeof useTiffinCheckout>;
  children: React.ReactNode;
}) {
  const t = useT();
  const { quote, total } = checkout;
  return (
    <View style={styles.pinned}>
      <View style={styles.summary}>
        <View style={styles.summaryText}>
          <Text variant="caption" tone="tertiary">{t('Total Amount')}</Text>
          {quote.error ? (
            <Text variant="caption" tone="danger" testID="tiffin-quote-error">
              {t(quote.error.message)}
            </Text>
          ) : (
            <Text variant="caption" tone="secondary" numberOfLines={3} testID="tiffin-total-sum">
              {quote.data ? sum(quote.data) : '—'}
            </Text>
          )}
        </View>
        {quote.isFetching ? (
          <Skeleton width={96} height={24} />
        ) : (
          <Text variant="amount" tone="brand" testID="tiffin-total">
            {total ? formatMoney(total) : '—'}
          </Text>
        )}
      </View>
      {children}
    </View>
  );
}

const styles = StyleSheet.create({
  pinned: {
    gap: space.md,
  },
  summary: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.md,
    backgroundColor: colors.bg.surface,
    borderRadius: radius.lg,
    padding: space.md,
  },
  summaryText: {
    flex: 1,
    gap: space.xxs,
  },
});
